import React, { useState } from "react"
import { Sidebar } from "../components/ui/sidebar"
import { CareerPathHeader } from "../components/ui/carreer-path/career-path-header"
import { CareerPathBanner } from "../components/ui/career-path-banner"
import { CareerPathCard } from "../components/ui/carreer-path/career-path-card"
import { AddCareerPathCard } from "../components/ui/carreer-path/add-career-path"
import { UniversalSearch } from "../components/ui/universal-search"
import {AIChatBox} from "../components/ui/ai-chat-box";
import ProtectedRoute from "../components/ProtectedRoute"

const initialCareerPaths = [
  {
    id: "1",
    category: "Scientist",
    categoryColor: "text-blue-500",
    title: "Marine Biologist",
    description: "Explore the mysteries of the deep ocean and its amazing creatures.",
    imageUrl: "/marine-biologist.avif?key=marinebiologist",
    progress: 60,
  },
  {
    id: "2",
    category: "Artist",
    categoryColor: "text-purple-500",
    title: "Animator",
    description: "Bring characters and stories to life through the magic of animation.",
    imageUrl: "/animators.avif?key=animator",
    progress: 35,
  },
  {
    id: "3",
    category: "Engineer",
    categoryColor: "text-red-500",
    title: "Game Developer",
    description: "Create amazing worlds and exciting challenges in video games.",
    imageUrl: "/game-developer.avif?key=gamedev",
    progress: 80,
  },
  {
    id: "4",
    category: "Doctor",
    categoryColor: "text-green-500",
    title: "Veterinarian",
    description: "Help sick and injured animals get back on their paws.",
    imageUrl: "/vet-doctor.avif?key=vet",
    progress: 15,
  },
  {
    id: "5",
    category: "Creator",
    categoryColor: "text-orange-500",
    title: "Chef",
    description: "Create delicious meals that make people happy every day.",
    imageUrl: "/chef.avif?key=chef",
    progress: 95,
  },
  {
    id: "6",
    category: "Explorer",
    categoryColor: "text-teal-500",
    title: "Astronaut",
    description: "Explore the vast universe and discover new worlds beyond Earth.",
    imageUrl: "/astronaut.avif?key=astronaut",
    progress: 45,
  },
]

export default function CareerPathPage() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [careerPaths, setCareerPaths] = useState(initialCareerPaths)

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen)
  const closeSidebar = () => setIsSidebarOpen(false)

  const handleDelete = (id) => {
    setCareerPaths(careerPaths.filter((path) => path.id !== id))
  }

  const handleExplore = (id) => {
    console.log(`Exploring career path: ${id}`)
  }

  const handleAddMore = () => {
    console.log("Add more career paths")
  }

  const handleQuizClick = () => {
    console.log("Taking the fun quiz")
  }

   const handleSearch = (query) => {
    console.log("Universal search for:", query)
  }

  return (
     <ProtectedRoute>
      <div className="flex min-h-screen bg-slate-200">
        {/* Universal Search Component */}
        <UniversalSearch onSearch={handleSearch} />
        
        {/* Sidebar */}
        <Sidebar/>

        <AIChatBox />

        {/* Main content */}
        <div className="flex-1 lg:ml-64 transition-all duration-300 ease-in-out">
          {/* Mobile Overlay */}
          {isSidebarOpen && (
            <div
              className="fixed inset-0 z-40 bg-black/10 backdrop-blur-sm md:hidden transition-all duration-300"
              onClick={closeSidebar}
            />
          )}

          <main
            className={`p-3 sm:p-4 md:p-6 lg:p-8 overflow-y-auto w-full min-h-screen transition-all duration-300 ${
              isSidebarOpen ? "blur-sm pointer-events-none md:blur-0 md:pointer-events-auto" : ""
            }`}
          >
            {/* Mobile Header */}
            <div className="md:hidden flex items-center gap-3 mb-4 pb-4 border-b border-slate-200">
              <button
                onClick={toggleSidebar}
                className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Toggle menu"
              >
                <svg
                  className="w-6 h-6 text-slate-700"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <div className="flex-1">
                <h1 className="text-lg font-bold text-slate-900">Career Paths</h1>
                <p className="text-sm text-slate-600">Welcome back, Alex</p>
              </div>
            </div>

            <div className="max-w-6xl mx-auto">
              {/* Desktop Header */}
              <div className="hidden md:block">
                <CareerPathHeader userName="Alex" />
              </div>

              {/* Title Section */}
              <div className="flex flex-wrap items-center justify-between gap-3 sm:gap-4 mb-6 sm:mb-8">
                <div className="flex flex-col gap-1 sm:gap-2">
                  <h1 className="text-2xl sm:text-3xl md:text-4xl font-black tracking-tighter text-slate-900">
                    Career Paths
                  </h1>
                  <p className="text-sm sm:text-base text-slate-500">
                    Your journey to a fun future starts here!
                  </p>
                </div>
                <div className="flex h-8 sm:h-9 items-center justify-center rounded-full bg-blue-600/20 px-3 sm:px-4">
                  <p className="text-xs sm:text-sm font-bold text-blue-600">
                    Showing {careerPaths.length} Paths
                  </p>
                </div>
              </div>

              {/* Banner */}
              <div className="mb-6 sm:mb-8 overflow-x-auto">
                <CareerPathBanner onQuizClick={handleQuizClick} />
              </div>

              {/* Career Paths Grid */}
              <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {careerPaths.map((path) => (
                  <CareerPathCard
                    key={path.id}
                    id={path.id}
                    category={path.category}
                    categoryColor={path.categoryColor}
                    title={path.title}
                    description={path.description}
                    imageUrl={path.imageUrl}
                    progress={path.progress}
                    onExplore={() => handleExplore(path.id)}
                    onDelete={handleDelete}
                  />
                ))}

                <AddCareerPathCard onAdd={handleAddMore} />
              </div>
            </div>
          </main>
        </div>
      </div>
     </ProtectedRoute>
  )
}